import { useTheme } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';

import ClearIcon from '@mui/icons-material/Cancel';
import { SfLocationLabelText, SfLabelText, SfAccordionSummary } from './Filter.style';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { genderFacet, minAgeFacet } from 'lib/utils/coveo/headlessFacets';
import { useEffect, useState, ChangeEvent } from 'react';
import { FacetState } from '@coveo/headless';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import headlessEngine from 'lib/utils/coveo/headlessEngine';
import { urlManagerController } from 'lib/utils/coveo/urlManager';
import { setDayOfBirth } from 'src/features/coveoSlice';
import moment from 'moment';

const GenderFilter = ({ data, controller }: any) => {
  const [state, setState] = useState<FacetState>(controller.state);
  const [selectedValue, setSelectedValue] = useState('');

  const onChange = (_: any, value: string) => {
    controller.deselectAll();
    const facetValue = value?.split(' (')[0];
    if (facetValue) {
      controller.toggleSelect({ value: facetValue, state: 'selected' });
    }
  };

  const onInputChange = (_: any, value: string) => {
    setSelectedValue(value);
  };

  useEffect(() => controller.subscribe(() => setState(controller.state)), [controller]);

  useEffect(() => {
    if (selectedValue && state.hasActiveValues) {
      const selected = state?.values?.find(
        (item) =>
          item.value === selectedValue.split(' ')[0] &&
          item.numberOfResults === Number(selectedValue.split('(')[1].split(' ')[0])
      );
      if (selected) {
        setSelectedValue(`${selected?.value} (${selected?.numberOfResults} results)`);
      } else {
        setSelectedValue('');
      }
    }
    if (selectedValue && !state.hasActiveValues) {
      setSelectedValue('');
    }

    if (state.hasActiveValues && !selectedValue) {
      const selected = state?.values?.find((item) => item.state === 'selected');
      setSelectedValue(`${selected?.value} (${selected?.numberOfResults} results)`);
    }
  }, [state?.values, controller, state.hasActiveValues, selectedValue]);

  return (
    <>
      <FormControl fullWidth>
        <SfLabelText id={data?.key}>
          {' '}
          <Text field={data?.fields?.label}></Text>
        </SfLabelText>
        <Autocomplete
          placeholder={data?.key}
          id={'sort-by-gender'}
          value={selectedValue}
          onChange={onChange}
          onInputChange={onInputChange}
          options={state?.values?.map((item) => `${item.value} (${item.numberOfResults} results)`)}
          isOptionEqualToValue={(_, value) => typeof value === 'string'}
          sx={{ width: '100%', '& input': { color: (theme) => theme.palette.neutral7.main } }}
          size="small"
          renderInput={(params) => <TextField {...params} placeholder="Select gender" />}
        />
      </FormControl>
    </>
  );
};

const DOBFilter = ({ data, controller }: any) => {
  const [, setState] = useState<FacetState>(controller.state);
  const dispatch = useDispatch();
  const value = useSelector((state: RootState) => state.coveoResult.dayOfBirth);
  const [error, setError] = useState(false);
  const userProfile = useSelector((state: RootState) => state.crProfile);
  const profileDetails = userProfile?.profileData?.basicDetails;

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 4) {
      dispatch(setDayOfBirth(e.target.value));

      if (!isNaN(+value) && (value.length === 4 || value.length === 0)) {
        setError(false);
      } else {
        setError(true);
      }
    }
  };

  useEffect(() => controller.subscribe(() => setState(controller.state)), [controller]);

  useEffect(() => {
    if (profileDetails?.dob) {
      const dobDate = moment.parseZone(profileDetails?.dob).format();
      const dobYear = moment.parseZone(dobDate).year();
      dispatch(setDayOfBirth(String(dobYear)));
    }
  }, [profileDetails?.dob]);

  return (
    <>
      <SfLabelText>
        {' '}
        <Text field={data?.fields?.label}></Text>
      </SfLabelText>
      <TextField
        value={value || ''}
        placeholder={data?.fields?.placeholder?.value}
        size="small"
        id="outlined-start-adornment"
        sx={{
          width: '100%',
          '& input': {
            color: (theme) => theme.palette.neutral7.main,
          },
          '& .MuiFormHelperText-root': {
            marginLeft: 0,
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ color: (theme) => theme.palette.neutral6.main }}>
              <ClearIcon
                style={{
                  cursor: 'pointer',
                  visibility: value ? 'visible' : 'hidden',
                }}
                onClick={() => dispatch(setDayOfBirth(''))}
              />
            </InputAdornment>
          ),
        }}
        onChange={onChange}
        error={error}
        helperText={error ? 'Enter a number and make sure it consists of 4 digits' : ''}
      />
    </>
  );
};

const ParticipantDetailsFilter = (props: any): JSX.Element => {
  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down(960));
  const userProfile = useSelector((state: RootState) => state.crProfile);
  const profileDetails = userProfile?.profileData?.basicDetails;

  const { heading } = props.fields;
  const PARTICIPANTS_FIELDS = {
    GENDER: 'Gender',
    DOB: 'yob',
  };

  const participantFields: any = {};
  props?.fields?.data?.forEach((item: any) => {
    participantFields[item?.fields?.key?.value] = {
      key: item?.fields?.key?.value,
      fields: item?.fields,
    };
  });
  const FindProfileGenderFilter = (gender?: string) => {
    switch (gender?.toLowerCase()) {
      case 'male':
        return 'Male';
      case 'female':
        return 'Female';
      default:
        return 'All';
    }
  };
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const urlFragment = window.location.hash.slice(1);
    const urlManager = urlManagerController(headlessEngine, urlFragment);
    let queryString = '';

    if (!!profileDetails?.gender) {
      const profileGender = FindProfileGenderFilter(profileDetails?.gender);
      queryString += `&f-trial_gender_list=${profileGender}`;
    }
    urlManager.synchronize(`#${queryString}&${urlManager.state.fragment}`);
  }, [profileDetails?.gender]);

  return (
    <Grid item sx={{ marginTop: '1rem' }}>
      <Accordion disableGutters defaultExpanded={isTabletOrMobile ? false : true}>
        <SfAccordionSummary
          sx={{ margin: 0 }}
          expandIcon={
            <ChevronRightIcon
              sx={{
                color: (theme) => theme.palette.neutral8.main,
                height: '32px',
                width: '32px',
              }}
            />
          }
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <SfLocationLabelText>
            <Text field={heading}></Text>
          </SfLocationLabelText>
        </SfAccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <GenderFilter
            data={participantFields[PARTICIPANTS_FIELDS.GENDER]}
            controller={genderFacet}
          />
          <DOBFilter data={participantFields[PARTICIPANTS_FIELDS.DOB]} controller={minAgeFacet} />
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};

export default ParticipantDetailsFilter;
